import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { getProduct, submitEnquiry } from '../services/api';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', quantity: '', message: ''
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await getProduct(productId);
        setProduct(res.data);
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E3A6F]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Product not found</h2>
          <Link to="/products">
            <Button className="bg-[#2E3A6F] hover:bg-[#1f2747]">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const getImageUrl = (img) => {
    if (!img?.url) return null;
    return img.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitEnquiry({
        ...formData,
        subject: `Product Enquiry: ${product.name}`,
        product_id: product.id,
        message: `Product: ${product.name}\nQuantity: ${formData.quantity}\n\n${formData.message}`
      });
      toast.success('Enquiry submitted successfully! We will contact you soon.');
      setEnquiryOpen(false);
      setFormData({ name: '', email: '', phone: '', company: '', quantity: '', message: '' });
    } catch {
      toast.error('Failed to submit enquiry. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="product-detail-page">
      <section className="py-6 sm:py-12 lg:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center text-gray-600 hover:text-[#2E3A6F] mb-4 sm:mb-8 transition-colors group">
            <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={20} />
            Back to Products
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
            {/* Images */}
            <div className="space-y-3 sm:space-y-4">
              <div className="relative overflow-hidden rounded-2xl border bg-white">
                {product.images?.length > 0 ? (
                  <img
                    src={getImageUrl(product.images[selectedImage])}
                    alt={product.name}
                    className="w-full aspect-square sm:aspect-[4/3] lg:h-[500px] lg:aspect-auto object-contain p-2 sm:p-4"
                    style={{ imageRendering: 'auto' }}
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://placehold.co/800x600/e5e7eb/6b7280?text=Image+not+available';
                    }}
                    data-testid="product-detail-main-image"
                  />
                ) : (
                  <div className="w-full aspect-square sm:aspect-[4/3] lg:h-[500px] lg:aspect-auto bg-gradient-to-br from-[#2E3A6F] to-[#1a1f3a] flex items-center justify-center">
                    <Package className="text-white/30" size={80} />
                  </div>
                )}
              </div>
              {product.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  {product.images.map((img, i) => (
                    <button
                      key={img.url || i}
                      onClick={() => setSelectedImage(i)}
                      className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 bg-white transition-all ${selectedImage === i ? 'border-[#2E3A6F]' : 'border-gray-200 opacity-60 hover:opacity-100'}`}
                      data-testid={`product-thumb-${i}`}
                    >
                      <img src={getImageUrl(img)} alt={`${product.name} ${i + 1}`} className="w-full h-full object-contain p-1" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex gap-2 flex-wrap">
                  {product.is_featured && <span className="text-xs px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">Featured</span>}
                  {product.is_new_arrival && <span className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium">New Arrival</span>}
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight" data-testid="product-name">{product.name}</h1>
                <p className="text-base sm:text-lg text-gray-600">{product.description}</p>
                {product.sku && <p className="text-sm text-gray-500">SKU: {product.sku}</p>}
              </div>

              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#2E3A6F] hover:bg-[#1f2747]"
                onClick={() => setEnquiryOpen(true)}
                data-testid="enquire-now-btn"
              >
                <Package className="mr-2" size={20} />
                Enquire Now
              </Button>

              {/* Features */}
              {product.features?.length > 0 && (
                <Card className="border-0 bg-white">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">Key Features</h3>
                    <ul className="space-y-3">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle2 className="text-[#2E3A6F] mr-3 mt-0.5 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Applications */}
              {product.applications && (
                <Card className="border-0 bg-gradient-to-br from-[#2E3A6F] to-[#1f2747] text-white">
                  <CardContent className="p-6 space-y-3">
                    <h3 className="text-xl font-semibold">Applications</h3>
                    <p className="text-white/90">{product.applications}</p>
                  </CardContent>
                </Card>
              )}

              {/* Catalog PDF */}
              {product.catalog_pdf && (
                <a
                  href={product.catalog_pdf.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${product.catalog_pdf}` : product.catalog_pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#2E3A6F] font-medium hover:underline"
                >
                  Download Product Catalog (PDF)
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Dialog */}
      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Product Enquiry</DialogTitle>
            <DialogDescription>
              Fill in your details and we&apos;ll get back to you shortly with pricing and availability.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" data-testid="enquiry-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" data-testid="enquiry-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="enquiry-phone" />
              </div>
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Your Company" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Quantity Required</Label>
              <Input value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="e.g., 100 pieces" />
            </div>
            <div className="space-y-2">
              <Label>Additional Requirements</Label>
              <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us more about your requirements..." rows={4} />
            </div>
            <div className="bg-[#2E3A6F]/10 rounded-lg p-4">
              <p className="text-sm text-gray-700"><strong>Product:</strong> {product.name}</p>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-[#2E3A6F] hover:bg-[#1f2747]" data-testid="submit-enquiry-btn">Submit Enquiry</Button>
              <Button type="button" variant="outline" onClick={() => setEnquiryOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetail;
